import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import './styles.css';
import { AppProvider, useApp } from './data/AppProvider';
import { AppShell } from './components/AppShell';
import { Spinner } from './components/ui';
import { SignInScreen } from './screens/SignIn';
import { AuthConfirmScreen } from './screens/AuthConfirm';
import { NowScreen } from './screens/Now';

// Secondary screens load on demand to keep the initial bundle small.
const TripsScreen = lazy(() => import('./screens/Trips').then((m) => ({ default: m.TripsScreen })));
const NewTripScreen = lazy(() => import('./screens/NewTrip').then((m) => ({ default: m.NewTripScreen })));
const TripScreen = lazy(() => import('./screens/Trip').then((m) => ({ default: m.TripScreen })));
const HomeScreen = lazy(() => import('./screens/Ask').then((m) => ({ default: m.HomeScreen })));
const AskRedirect = lazy(() => import('./screens/Ask').then((m) => ({ default: m.AskRedirect })));
const DocumentsScreen = lazy(() => import('./screens/Documents').then((m) => ({ default: m.DocumentsScreen })));
const ExploreScreen = lazy(() => import('./screens/Explore').then((m) => ({ default: m.ExploreScreen })));
const GroupsScreen = lazy(() => import('./screens/Groups').then((m) => ({ default: m.GroupsScreen })));
const GroupScreen = lazy(() => import('./screens/Group').then((m) => ({ default: m.GroupScreen })));
const JoinScreen = lazy(() => import('./screens/Join').then((m) => ({ default: m.JoinScreen })));
const PrivacyScreen = lazy(() => import('./screens/Legal').then((m) => ({ default: m.PrivacyScreen })));
const TermsScreen = lazy(() => import('./screens/Legal').then((m) => ({ default: m.TermsScreen })));
const SettingsScreen = lazy(() => import('./screens/Settings').then((m) => ({ default: m.SettingsScreen })));

function RequireSession() {
  const { session, sessionLoading } = useApp();
  const loc = useLocation();
  if (sessionLoading) return <Spinner label="Opening Elio Travels" />;
  if (!session) return <Navigate to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <Suspense fallback={<Spinner />}><Outlet /></Suspense>;
}

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInScreen /> },
  { path: '/auth/confirm', element: <AuthConfirmScreen /> },
  { path: '/privacy', element: <Suspense fallback={<Spinner />}><PrivacyScreen /></Suspense> },
  { path: '/terms', element: <Suspense fallback={<Spinner />}><TermsScreen /></Suspense> },
  {
    element: <RequireSession />,
    children: [
      { path: '/join/:token', element: <JoinScreen /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomeScreen /> },
          { path: 'c/:conversationId', element: <HomeScreen /> },
          { path: 'today', element: <NowScreen /> },
          { path: 'trips', element: <TripsScreen /> },
          { path: 'trips/new', element: <NewTripScreen /> },
          { path: 'trips/:tripId', element: <TripScreen /> },
          { path: 'trips/:tripId/:tab', element: <TripScreen /> },
          { path: 'ask', element: <AskRedirect /> },
          { path: 'ask/:conversationId', element: <AskRedirect /> },
          { path: 'documents', element: <DocumentsScreen /> },
          { path: 'explore', element: <ExploreScreen /> },
          { path: 'explore/:section', element: <ExploreScreen /> },
          { path: 'groups', element: <GroupsScreen /> },
          { path: 'groups/:groupId', element: <GroupScreen /> },
          { path: 'settings', element: <SettingsScreen /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppProvider>
      <RouterProvider router={router} />
    </AppProvider>
  </StrictMode>,
);
